import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Loader2, Video, Headphones, ChefHat, FileText, Users } from 'lucide-react';
import { useUserFavoritesContext } from '../../contexts/UserFavoritesContext';
import { detectKindFromUrl } from '../../utils/urlUtils';
import { useTrackedLinkModal } from '../../hooks';
import { ContentModal, Toast } from '../ui';

interface Citation {
  url: string;
  title: string;
  description?: string;
}

interface CitationsProps {
  citations: Citation[];
  annotations?: any[];
  messageContent?: string; // Full assistant message text
  sessionId?: string;
}

export const Citations: React.FC<CitationsProps> = ({ 
  citations, 
  messageContent = '',
  sessionId
}) => {
  const navigate = useNavigate();
  
  const { createFavorite, creating, refresh, getResourceByUrl, toggleFavoriteFlag } = useUserFavoritesContext();
  const { isOpen, url, title, showOpenInNewTab, allowCredentials, trustDomain, openLink, closeModal } = useTrackedLinkModal();
  const [togglingCitation, setTogglingCitation] = useState<string | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const getKindIcon = (kind: string) => {
    switch (kind) {
      case 'video': return Video;
      case 'audio': return Headphones;
      case 'recipe': return ChefHat;
      case 'article': return FileText;
      case 'meeting': return Users;
      default: return FileText;
    }
  };

  const handleToggleFavorite = useCallback(async (citation: Citation) => {
    if (togglingCitation) return;
    setTogglingCitation(citation.url);

    try {
      const existing = getResourceByUrl(citation.url);
      if (existing) {
        await toggleFavoriteFlag(existing.id);
        const wasFavorited = existing.payload?.metadata?.isFavorited === true;
        setToastMessage(wasFavorited ? `"${citation.title}" removed from favorites` : `"${citation.title}" added to favorites`);
      } else {
        const kind = detectKindFromUrl(citation.url);
        await createFavorite({
          kind,
          payload: {
            itemId: citation.url,
            title: citation.title,
            description: citation.title,
            url: citation.url,
            metadata: {
              link: citation.url,
              details: messageContent,
              source: 'citation',
              isFavorited: true,
              citationTitle: citation.title,
              addedAt: new Date().toISOString()
            }
          }
        });
        setToastMessage(`"${citation.title}" added to favorites`);
      }
      setToastVisible(true);
      await refresh();
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    } finally {
      setTogglingCitation(null);
    }
  }, [togglingCitation, getResourceByUrl, toggleFavoriteFlag, createFavorite, messageContent, refresh]);

  if (!citations || citations.length === 0) {
    return null;
  }

  // Extract citation numbers from annotations
  const getCitationNumber = (_citationUrl: string, index: number): string => {
    // Simply use the sequential index from the citations array
    // Citations should be numbered 1, 2, 3, 4, 5... based on their order
    return (index + 1).toString();
  };

  return (
    <div className="mt-3 pt-3 border-t border-rm-neutral-600/20">
      <div className="flex flex-wrap gap-2">
        {citations.map((citation, index) => {
          const favoriteRecord = getResourceByUrl(citation.url);
          const isFavorited = favoriteRecord?.payload?.metadata?.isFavorited === true;
          const isToggling = togglingCitation === citation.url;
          const kind = detectKindFromUrl(citation.url);
          const citationNumber = getCitationNumber(citation.url, index);
          const KindIcon = getKindIcon(kind);
          
          return (
            <div 
              key={`${citation.url}-${index}`}
              className="flex items-center gap-2 p-2 bg-rm-bg-50 rounded-card border border-rm-neutral-600/10 text-xs max-w-sm"
            >
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <span className="font-medium text-rm-primary-700 flex-shrink-0">#{citationNumber}</span>
                <KindIcon className="w-3 h-3 flex-shrink-0 text-rm-neutral-600" />
                <button
                    onClick={() => openLink(citation.url, citation.title)}
                    className="text-rm-primary-700 hover:text-rm-accent-600 font-medium truncate text-left transition-colors"
                    title={`Open ${citation.title}`}
                  >
                    {citation.title}
                  </button>
              </div>
              
              {sessionId && (
                <button
                  onClick={() => handleToggleFavorite(citation)}
                  disabled={isToggling || creating}
                  className={`flex-shrink-0 p-1 rounded-full transition-colors ${
                    isFavorited
                      ? 'text-red-500 hover:text-red-400'
                      : 'text-gray-400 hover:text-red-500 hover:bg-gray-200'
                  } ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={isFavorited ? 'Remove from My Resources' : 'Add to My Resources'}
                >
                  {isToggling ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Heart className={`w-3.5 h-3.5 ${isFavorited ? 'fill-current' : ''}`} />
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Content Modal */}
      <ContentModal
        url={url}
        title={title}
        isOpen={isOpen}
        onClose={closeModal}
        showOpenInNewTab={showOpenInNewTab}
        allowCredentials={allowCredentials}
        trustDomain={trustDomain}
      />

      {/* Toast notification for added favorites */}
      {toastVisible && (
        <Toast
          message={toastMessage}
          type="success"
          icon="heart"
          duration={4000}
          onClose={() => setToastVisible(false)}
          action={{
            label: 'View all',
            onClick: () => {
              setToastVisible(false);
              navigate('/favorites');
            }
          }}
        />
      )}
    </div>
  );
};
